'use client';

import React, { useCallback, useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faRoute,
  faXmark,
  faWandMagicSparkles,
  faTrash,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/pro-solid-svg-icons';
import { PulseIconButton, PulseButton, Input } from 'src/components';
import useRouteEditorLogic from 'src/hooks/routes/useRouteEditorLogic';
import { StopList } from '../StopList';
import { DirectionsList } from '../DirectionsList';
import { RouteListPanel } from '../RouteListPanel';

/**
 * RoutePanel - A lightweight panel for route creation/editing
 *
 * Renders as an absolute-positioned panel over the map, allowing
 * map interactions to continue while the panel is open.
 *
 * Uses useRouteEditorLogic for all business logic.
 */
const RoutePanel: React.FC = () => {
  const {
    mode,
    tempLabel,
    tempStops,
    directions,
    isLoadingDirections,
    isEditing,
    canSave,
    selectedRouteId,
    isOpen,
    isLoading,
    isSaving,
    isDeleting,
    handleLabelChange,
    handleReorder,
    handleRemove,
    handleSave,
    handleDelete,
    handleCancel,
    handleOptimize,
  } = useRouteEditorLogic();

  // Local UI state for minimize toggle
  const [isMinimized, setIsMinimized] = useState(false);

  const toggleMinimize = useCallback(() => {
    setIsMinimized((prev) => !prev);
  }, []);

  // When idle, show the routes list
  if (mode === 'idle') {
    return (
      <div
        className="absolute top-4 right-4 max-h-[calc(100%-2rem)] w-[340px] bg-white rounded-lg shadow-xl border border-neutral-200 flex flex-col z-50"
        data-testid="route-panel"
      >
        <RouteListPanel className="flex-1 min-h-0 overflow-hidden" />
      </div>
    );
  }

  if (!isOpen) return null;

  // Shared header component for consistency
  const renderHeader = () => (
    <div className="p-3 flex items-center gap-3 border-b border-neutral-200 shrink-0">
      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-sky-100 shrink-0">
        <Icon icon={faRoute} className="w-5 h-5 text-sky-600" />
      </div>
      <div className="flex-1 min-w-0">
        {isMinimized ? (
          <>
            <p className="text-sm font-semibold text-neutral-900 truncate">
              {tempLabel || 'New Route'}
            </p>
            <p className="text-xs text-neutral-500">
              {tempStops.length} {tempStops.length === 1 ? 'stop' : 'stops'}
              {directions && ` · ${directions.totalDistanceText}`}
            </p>
          </>
        ) : (
          <Input
            value={tempLabel}
            onChange={handleLabelChange}
            placeholder="Enter route name..."
            variant="plain"
            size="lg"
            className="font-semibold text-lg"
            aria-label="Route name"
            data-testid="route-label-input"
          />
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <PulseIconButton
          variant="plain"
          color="neutral"
          size="sm"
          onClick={toggleMinimize}
          icon={isMinimized ? faChevronDown : faChevronUp}
          tooltip={isMinimized ? 'Expand panel' : 'Minimize panel'}
          aria-label={isMinimized ? 'Expand panel' : 'Minimize panel'}
          data-testid={
            isMinimized
              ? 'route-panel-expand-button'
              : 'route-panel-minimize-button'
          }
        />
        <PulseIconButton
          variant="plain"
          color="neutral"
          size="sm"
          onClick={handleCancel}
          icon={faXmark}
          tooltip="Close panel"
          aria-label="Close panel"
          data-testid="route-panel-close-button"
        />
      </div>
    </div>
  );

  // Minimized view - just the header
  if (isMinimized) {
    return (
      <div
        className="absolute top-4 right-4 w-[340px] bg-white rounded-lg shadow-xl border border-neutral-200 z-50"
        data-testid="route-panel"
      >
        {renderHeader()}
      </div>
    );
  }

  // Full expanded view
  return (
    <div
      className="absolute top-4 right-4 max-h-[calc(100%-2rem)] w-[340px] bg-white rounded-lg shadow-xl border border-neutral-200 flex flex-col z-50"
      data-testid="route-panel"
    >
      {renderHeader()}

      {/* Actions */}
      <div className="p-4 border-b border-neutral-200 flex items-center gap-2 shrink-0">
        <PulseButton
          variant="outline"
          color="neutral"
          size="sm"
          onClick={handleOptimize}
          disabled={tempStops.length < 3}
          loading={isLoadingDirections}
          aria-label="Optimize route order"
          data-testid="route-optimize-button"
          startDecorator={faWandMagicSparkles}
        >
          Optimize
        </PulseButton>
        {isEditing && selectedRouteId && (
          <PulseButton
            variant="outline"
            color="danger"
            size="sm"
            onClick={handleDelete}
            loading={isDeleting}
            aria-label="Delete route"
            data-testid="route-delete-button"
            startDecorator={faTrash}
          >
            Delete
          </PulseButton>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="p-4">
          <h3 className="text-sm font-semibold text-neutral-700 mb-3">
            Stops ({tempStops.length})
          </h3>
          {tempStops.length === 0 ? (
            <p className="text-sm text-neutral-500 italic">
              Click markers on the map or use the add button on results to add
              stops
            </p>
          ) : (
            <StopList
              stops={tempStops}
              onReorder={handleReorder}
              onRemove={handleRemove}
              isLoading={isLoading}
            />
          )}
        </div>

        {/* Directions */}
        {tempStops.length >= 2 && directions && (
          <div className="p-4 border-t border-neutral-200">
            <h3 className="text-sm font-semibold text-neutral-700 mb-3">
              Directions
            </h3>
            <DirectionsList directions={directions} />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-neutral-200 flex items-center justify-end gap-2 shrink-0">
        <PulseButton
          variant="outline"
          color="neutral"
          onClick={handleCancel}
          disabled={isSaving}
          data-testid="route-cancel-button"
        >
          Cancel
        </PulseButton>
        <PulseButton
          variant="solid"
          color="primary"
          onClick={handleSave}
          loading={isSaving}
          disabled={!canSave || isSaving}
          data-testid="route-save-button"
        >
          {isEditing ? 'Save Changes' : 'Create Route'}
        </PulseButton>
      </div>
    </div>
  );
};

export default RoutePanel;
